import { TweakPanel } from '@/commons/TweakPanel';
import { WorldDebugPanel } from '@/commons/world/WorldDebugPanel';
import { ArcRotateCamera } from '@/commons/camera/ArcRotateCamera';
import { TrackballCamera } from '@/commons/camera/TrackballCamera';
import { Wedding3DWorld } from '@/littleWedding/Wedding3DWorld';
import { CameraHelper } from 'three';
import { type FolderApi, SliderInputBindingApi } from 'tweakpane';
import type { TCameraParams } from '@/commons/camera/TCameraParams';
import type { IPerspCamWithControls } from '@/commons/camera/IPerspCamWithControls';

const CameraDebugPanel = {
    params: {
        folderName: 'Camera',
    },

    set displayCameraDebugPanel(active: boolean) {
        if (active) {
            CameraDebugPanel.initMenu();
        } else {
            TweakPanel.removeFolderByName(this.params.folderName);
        }
    },

    get cameraFolder() {
        const cameraFolderExist = TweakPanel.getFolderOrTabByName(this.params.folderName);
        if (cameraFolderExist) {
            return cameraFolderExist as FolderApi;
        }
        return WorldDebugPanel.globalTab?.addFolder({
            title: CameraDebugPanel.params.folderName,
            expanded: false,
        });
    },

    get camera(): ArcRotateCamera | undefined {
        return Wedding3DWorld.instance?.camera;
    },

    initMenu() {
        const { camera, cameraFolder } = CameraDebugPanel;
        if (!camera) {
            return;
        }
        this.cameraSwitcher();

        this.cameraRotatePanel();
        this.cameraZoomPanel();
        this.addHelperControlPanel(camera);
        cameraFolder?.addBlade({
            view: 'separator',
        });
    },
    cameraSwitcher() {
        const { camera, cameraFolder } = CameraDebugPanel;
        if (!camera) {
            return;
        }
        cameraFolder?.addBinding(
            {
                get cameraType() {
                    return CameraDebugPanel.camera?.constructor.name || '';
                },
                set cameraType(value: string) {
                    let newCamera;
                    const duplicateParams: TCameraParams = {
                        name: CameraDebugPanel.camera?.name,
                        aspect: CameraDebugPanel.camera?.aspect,
                        fov: CameraDebugPanel.camera?.fov,
                        near: CameraDebugPanel.camera?.near,
                        far: CameraDebugPanel.camera?.far,
                        rendererDomElement: Wedding3DWorld.instance!.renderer.domElement,
                    };
                    switch (value) {
                        case 'ArcRotateCamera':
                            newCamera = Wedding3DWorld.instance ? new ArcRotateCamera(duplicateParams) : undefined;
                            break;
                        case 'TrackballCamera':
                            newCamera = Wedding3DWorld.instance ? new TrackballCamera(duplicateParams) : undefined;
                            break;
                        default:
                            break;
                    }
                    if (!newCamera) {
                        return;
                    }
                    if (!Wedding3DWorld.instance) {
                        return;
                    }
                    (newCamera as ArcRotateCamera).position.copy(CameraDebugPanel.camera!.position);
                    (newCamera as ArcRotateCamera).lookAt(CameraDebugPanel.camera!.controls.target);
                    Wedding3DWorld.instance.newRendererCamera = newCamera as ArcRotateCamera;
                },
            },
            'cameraType',
            {
                label: 'Camera Type',
                value: 'ArcRotateCamera',
                options: {
                    ArcRotateCamera: 'ArcRotateCamera',
                    TrackballCamera: 'TrackballCamera',
                },
            },
        );
    },
    cameraRotatePanel() {
        const rotateFolder = this.cameraFolder?.addFolder({
            title: 'Rotate',
            expanded: false,
        });
        if (CameraDebugPanel.camera) {
            rotateFolder?.addBinding(
                {
                    get enableRotate() {
                        return CameraDebugPanel.camera?.controls.enableRotate || false;
                    },
                    set enableRotate(value: boolean) {
                        if (CameraDebugPanel.camera) {
                            CameraDebugPanel.camera.controls.enableRotate = value;
                        }
                    },
                },
                'enableRotate',
                {
                    label: 'Enable Rotate',
                },
            );
            rotateFolder?.addBinding(
                {
                    get rotateSpeed() {
                        return CameraDebugPanel.camera?.controls.rotateSpeed || 0;
                    },
                    set rotateSpeed(value: number) {
                        if (CameraDebugPanel.camera) {
                            CameraDebugPanel.camera.controls.rotateSpeed = value;
                        }
                    },
                },
                'rotateSpeed',
                {
                    label: 'Rotate Speed',
                    min: 0.1,
                    max: 10,
                    step: 0.1,
                },
            );
            CameraDebugPanel.camera.controls.addEventListener('change', () => {
                const rotateCameraFolder = TweakPanel.getFolderOrTabByName(
                    'Rotate',
                    TweakPanel.getFolderOrTabByName(this.params.folderName),
                ) as FolderApi;
                if (!rotateCameraFolder) {
                    return;
                }
                const finder = rotateCameraFolder.children.filter((child) =>
                    ['phiSlider', 'radiusSlider', 'thetaSlider'].includes((child as SliderInputBindingApi)?.tag || ''),
                );
                if (finder.length) {
                    finder.forEach((child) => {
                        (child as SliderInputBindingApi).refresh();
                    });
                }
            });
        }
    },
    cameraZoomPanel() {
        const zoomFolder = CameraDebugPanel.cameraFolder?.addFolder({
            title: 'Zoom',
            expanded: false,
        });
        zoomFolder?.addBinding(
            {
                get enableZoom() {
                    return CameraDebugPanel.camera?.controls.enableZoom || false;
                },
                set enableZoom(value: boolean) {
                    if (!CameraDebugPanel.camera) {
                        return;
                    }
                    CameraDebugPanel.camera.controls.enableZoom = value;
                },
            },
            'enableZoom',
            {
                label: 'Enable Zoom',
            },
        );
        zoomFolder?.addBinding(
            {
                get zoom() {
                    return CameraDebugPanel.camera?.zoom || 0;
                },
                set zoom(value: number) {
                    if (!CameraDebugPanel.camera) {
                        return;
                    }
                    CameraDebugPanel.camera.zoom = value;
                    CameraDebugPanel.camera.updateProjectionMatrix();
                },
            },
            'zoom',
            {
                label: 'Zoom',
                min: 0.5,
                max: 10,
                step: 0.1,
            },
        );
    },
    addHelperControlPanel(camera: ArcRotateCamera | IPerspCamWithControls | TrackballCamera) {
        const cameraHelperFolder = this.cameraFolder?.addFolder({ title: 'camera-helper', expanded: false });
        cameraHelperFolder?.addBinding(
            {
                shadowCamFolderName: `camera-helper-panel`,
                isHelperDisplay: false,
                get addHelper() {
                    return this.isHelperDisplay;
                },
                set addHelper(value) {
                    if (value) {
                        const cameraHelper = new CameraHelper(camera as ArcRotateCamera);
                        cameraHelper.name = `${(camera as ArcRotateCamera).name}-helper`;
                        Wedding3DWorld.instance?.scene?.add(cameraHelper);
                        const folder = cameraHelperFolder.addFolder({ title: this.shadowCamFolderName });
                        folder.addBinding(
                            {
                                get near() {
                                    return (camera as ArcRotateCamera).near;
                                },
                                set near(nearValue) {
                                    (camera as ArcRotateCamera).near = nearValue;
                                    (camera as ArcRotateCamera).updateProjectionMatrix();
                                    cameraHelper.update();
                                },
                            },
                            'near',
                            { min: 0, max: 10, step: 0.1 },
                        );
                        folder.addBinding(
                            {
                                get far() {
                                    return (camera as ArcRotateCamera).far;
                                },
                                set far(farValue) {
                                    (camera as ArcRotateCamera).far = farValue;
                                    (camera as ArcRotateCamera).updateProjectionMatrix();
                                    cameraHelper.update();
                                },
                            },
                            'far',
                            { min: 0, max: 25, step: 0.1 },
                        );
                    } else {
                        Wedding3DWorld.instance?.scene.remove(
                            Wedding3DWorld.instance?.scene.getObjectByName(`${(camera as ArcRotateCamera).name}-helper`) as CameraHelper,
                        );
                        TweakPanel.removeFolderByName(this.shadowCamFolderName);
                    }
                    this.isHelperDisplay = value;
                },
            },
            'addHelper',
            { label: 'Display Camera Helper' },
        );
    },
};

export { CameraDebugPanel };
